/**
 * CTRL_12 Configuration Panel - Purpose Limitation
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Collapse, Button, Alert, Spin, message, Select, Input, Form } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option} = Select;
const { TextArea } = Input;

const DATA_PURPOSES = [
  { value: 'training', label: 'Model Training' },
  { value: 'inference', label: 'Model Inference' },
  { value: 'testing', label: 'Testing/Validation' },
  { value: 'research', label: 'Research & Development' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'improvement', label: 'Service Improvement' }
];

function CTRL_12_ConfigPanel({ project, projectId, controlCode = 'CTRL_12' }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [configData, setConfigData] = useState({
    enabled: false,
    allowed_purposes: [],
    require_consent: true,
    require_documentation: true,
    consent_mechanisms: [],
    purpose_change_notification: true
  });

  useEffect(() => {
    if (projectId) {
      fetchConfig();
    }
  }, [projectId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await getControlConfig(projectId, controlCode);
      if (res.data) {
        setConfig(res.data);
        setConfigData({
          enabled: res.data.enabled || false,
          ...res.data.config
        });
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        control_code: controlCode,
        enabled: configData.enabled,
        config: {
          allowed_purposes: configData.allowed_purposes,
          require_consent: configData.require_consent,
          require_documentation: configData.require_documentation,
          consent_mechanisms: configData.consent_mechanisms,
          purpose_change_notification: configData.purpose_change_notification
        }
      };

      await saveControlConfig(projectId, controlCode, payload);
      showSuccess('Configuration saved successfully');
      fetchConfig();
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfigData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}><Spin size="large" /></div>;
  }

  return (
    <Card>
      <div style={{ marginBottom: '20px' }}>
        <h3>CTRL_12: Purpose Limitation Configuration</h3>
        <p style={{ color: '#666' }}>
          Configure purpose limitation checks to ensure data is only used for declared purposes
        </p>
      </div>

      <Collapse defaultActiveKey={configData.enabled ? ['1', '2'] : ['1']}>
        <Panel header="1. Enable Purpose Limitation Control" key="1">
          <Switch
            checked={configData.enabled}
            onChange={(checked) => updateConfig('enabled', checked)}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
          <p style={{ marginTop: '12px', color: '#666' }}>
            When enabled, events will be checked against allowed data purposes
          </p>
        </Panel>

        {configData.enabled && (
          <Panel header="2. Purpose Configuration" key="2">
            <Form layout="vertical">
              <Form.Item label="Allowed Purposes">
                <Select
                  mode="multiple"
                  value={configData.allowed_purposes || []}
                  onChange={(value) => updateConfig('allowed_purposes', value)}
                  style={{ width: '100%' }}
                  placeholder="Select allowed data purposes"
                >
                  {DATA_PURPOSES.map(purpose => (
                    <Option key={purpose.value} value={purpose.value}>
                      {purpose.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Consent Requirements">
                <div style={{ marginBottom: '12px' }}>
                  <Switch
                    checked={configData.require_consent}
                    onChange={(checked) => updateConfig('require_consent', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require user consent for data collection</span>
                </div>

                <div>
                  <Switch
                    checked={configData.require_documentation}
                    onChange={(checked) => updateConfig('require_documentation', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require purpose documentation</span>
                </div>
              </Form.Item>

              <Form.Item label="Consent Mechanisms">
                <Select
                  mode="tags"
                  value={configData.consent_mechanisms || []}
                  onChange={(value) => updateConfig('consent_mechanisms', value)}
                  style={{ width: '100%' }}
                  placeholder="Enter consent mechanisms (e.g., opt-in, cookie banner)"
                >
                  <Option value="opt-in">Opt-in</Option>
                  <Option value="opt-out">Opt-out</Option>
                  <Option value="cookie_banner">Cookie Banner</Option>
                  <Option value="terms_of_service">Terms of Service</Option>
                </Select>
              </Form.Item>

              <Form.Item label="Purpose Change Notification">
                <Switch
                  checked={configData.purpose_change_notification}
                  onChange={(checked) => updateConfig('purpose_change_notification', checked)}
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                />
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Notify users when data usage purpose changes
                </p>
              </Form.Item>
            </Form>
          </Panel>
        )}
      </Collapse>

      {configData.enabled && (
        <Alert
          message="Configuration Summary"
          description={
            <div>
              <div><strong>Allowed Purposes:</strong> {configData.allowed_purposes?.join(', ') || 'None'}</div>
              <div><strong>Consent Required:</strong> {configData.require_consent ? 'Yes' : 'No'}</div>
              <div><strong>Documentation Required:</strong> {configData.require_documentation ? 'Yes' : 'No'}</div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginTop: '16px' }}
        />
      )}

      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={saving}
        style={{ marginTop: '16px' }}
        size="large"
      >
        Save Configuration
      </Button>
    </Card>
  );
}

export default CTRL_12_ConfigPanel;
